import { useContext, useEffect } from "react";
import { SocketContext } from "../providers/SocketProvider/SocketProvider";
import {
  SocketAnswerAction,
  SocketApiActionType,
  SocketApiParams,
  SocketRequestAction,
} from "../types/socket.api.types";

// Сокет отправляет лишние сообщения с типами, которые обрабатывать не нужно
const ignoredMessageTypes = ["ping", "welcome", "confirm_subscription"];

/**
 * @param {number} channelId - id канала, для которого нужно перехватывать события. Хук будет отлавливать и обрабатывать только события,
 *                            связанные с этим конкретным каналом. Если параметр не указан, то будут обрабатываться события всех каналов
 *
 * @param {number} broadcastId - id трансляции, для которого нужно перехватывать события. Хук будет отлавливать и обрабатывать только события,
 *                            связанные с этим конкретной трансляцией. Если параметр не указан, то будут обрабатываться события всех трансляций
 *
 * @param {number} studioId - id студии, для которой нужно перехватывать события. Хук будет отлавливать и обрабатывать только события,
 *                            связанные с этой конкретной студией. Если параметр не указан, то будут обрабатываться события всех студий
 */
export const useSocketApi = ({
  channelId,
  broadcastId,
  studioId,
  onBroadcastStatusUpdated,
  onTwitchBitrateWarning,
  onBitrateWarning,
  onChannelStatusUpdated,
  onNewChatMessage,
  onVkCaptchaError,
  onVkFloodError,
  onVkUnknowError,
  onStudioSceneUpdated,
  onInvalidStreamKeyWarning,
  onNextTemplateRestartTime,
  onCheckTariffEnded,
  onStudioUpdated,
  onTelegramLinkedSuccess,
  onUpdateChannelUptime,
}: SocketApiParams = {}) => {
  const { webSocket } = useContext(SocketContext);

  const sendMessage = ({ payload, identifier, target_platform }: SocketRequestAction) => {
    if (!webSocket || webSocket.readyState !== webSocket.OPEN) return;

    webSocket.send(
      JSON.stringify({
        command: "message",
        data: JSON.stringify(payload),
        identifier,
        target_platform
      })
    );
  };

  useEffect(() => {
    if (!webSocket) return;

    const handleMessage = ({ data }: MessageEvent) => {
      const parsedData = JSON.parse(data);

      if (ignoredMessageTypes.includes(parsedData.type)) return;

      if (!parsedData.message?.action || !parsedData.message?.payload) return;

      const { action, payload } = parsedData.message as SocketAnswerAction;

      switch (action) {
        case SocketApiActionType.BROADCAST_STATUS_UPDATED: {
          const { broadcast_id } = payload;
          if (!broadcastId || broadcastId === broadcast_id) {
            onBroadcastStatusUpdated?.(payload);
          }
          break;
        }

        case SocketApiActionType.TARIFF_ENDED: {
          onCheckTariffEnded?.(payload);
          break;
        }

        case SocketApiActionType.TWITCH_BITRATE_WARNING: {
          const { broadcast_id, restream_template_id } = payload;
          if (broadcast_id === broadcastId || channelId === restream_template_id) {
            onTwitchBitrateWarning?.(payload);
          }
          break;
        }

        case SocketApiActionType.STUDIO_SCENE_UPDATED: {
          const { studio_id } = payload;
          if (studio_id === studioId) {
            onStudioSceneUpdated?.(payload);
          }
          break;
        }

        case SocketApiActionType.STUDIO_UPDATED: {
          onStudioUpdated?.(payload);
          break;
        }

        case SocketApiActionType.BITRATE_WARNING: {
          const { broadcast_id } = payload;
          if (broadcast_id === broadcastId) {
            onBitrateWarning?.(payload);
          }
          break;
        }

        case SocketApiActionType.INVALID_STREAM_KEY_WARNING: {
          const { broadcast_id } = payload;
          if (broadcast_id === broadcastId) {
            onInvalidStreamKeyWarning?.(payload);
          }
          break;
        }

        case SocketApiActionType.CHANNEL_STATUS_UPDATED: {
          const { restream_template, broadcast_id } = payload;
          const { id } = restream_template;

          if ((!broadcastId || broadcast_id === broadcastId) &&
            (!channelId || channelId === id)) {
            onChannelStatusUpdated?.(payload);
          }
          break;
        }

        case SocketApiActionType.UPDATE_CHANNEL_UPTIME: {
          const { templ_id, broadcast_id } = payload;

          if ((!broadcastId || broadcast_id === broadcastId) &&
            (channelId === templ_id)) {
            onUpdateChannelUptime?.(payload);
            break;
          }

          break;
        }
        case SocketApiActionType.NEXT_TEMPLATE_RESTART_TIME: {
          const { broadcast_id, template_id } = payload;

          if (broadcast_id === broadcastId && channelId === template_id) {
            onNextTemplateRestartTime?.(payload);
          }

          break;
        }

        case SocketApiActionType.NEW_CHAT_MESSAGE: {
          onNewChatMessage?.(payload);
          break;
        }

        case SocketApiActionType.TELEGRAM_CHAT_LINKED: {
          onTelegramLinkedSuccess?.(payload);
          break;
        }

        case SocketApiActionType.VK_CAPTCHA_ERROR: {
          onVkCaptchaError?.(payload);
          break;
        }

        case SocketApiActionType.VK_FLOOD_ERROR: {
          onVkFloodError?.(payload);
          break;
        }

        case SocketApiActionType.VK_UNKNOWN_ERROR: {
          onVkUnknowError?.(payload);
          break;
        }
      }
    };

    webSocket.addEventListener("message", handleMessage);

    return () => {
      webSocket.removeEventListener("message", handleMessage);
    };
  }, [
    webSocket,
    broadcastId,
    channelId,
    onBroadcastStatusUpdated,
    onTwitchBitrateWarning,
    onBitrateWarning,
    onChannelStatusUpdated,
    onNewChatMessage,
    onVkCaptchaError,
    onVkFloodError,
    onVkUnknowError,
    onInvalidStreamKeyWarning
  ]);

  return {
    sendMessage,
  };
};